import React, { useState } from "react";
import { Page, LoadingMsg, NavItem, TabPane } from "components/common";
import { compose } from "recompose";
import { Nav, TabContent, Button } from "reactstrap";
import Table from "./TableInvoices";
import { Trans } from "react-i18next";
import { XeroClient } from "services";
import { Link, Switch, Redirect } from "react-router-dom";
import { renderCountSpan } from "services/util";
import * as XLSX from 'xlsx';
import { NotificationManager } from "react-notifications";
import { createPortal } from 'react-dom';
import { invNotes as importantNotes } from '../../UserList/modelNames';
import ImportModal, { readFileAsync } from "components/ImportModal";
import { isString } from "lodash";
import { useQuery } from "react-query";
import { useNonReportedTotals } from "hooks/useNonReportedTotals";
import { useOrganisationContext } from "hooks";
import Warning from "./Warning";

const getTable = (Statuses, isReporting) => (props) => {
  const { selectedOrganisation } = useOrganisationContext();
  let raw = isReporting ? `Type="ACCREC" AND Date >= DateTime(${selectedOrganisation.onboarding_date})` : 'Type="ACCREC"'
  if (isReporting && selectedOrganisation.pos_contacts_ids) {
      const contactIds = selectedOrganisation.pos_contacts_ids?.split(',') || []
      const filterStrings = contactIds.map(contactId => `Contact.ContactID!=GUID("${contactId}")`);
      raw = `${raw} AND (` + filterStrings.join(" AND ") + ')'
  }
  return (
    <Table
      filters={{
        extra_params: {
          Statuses: isString(Statuses) ? Statuses.split(",") : Statuses,
        },
        raw: raw,
        Type: "ACCREC",
      }}
      status={Statuses}
      {...props}
    />
  )
};

const All = getTable("DRAFT,SUBMITTED,AUTHORISED,PAID");
const Draft = getTable("DRAFT");
const AwaitingApproval = getTable("SUBMITTED");
const Approved = getTable("AUTHORISED");
const Paid = getTable("PAID");
const Reporting = getTable("PAID,AUTHORISED", true);

export const List = ({ match }) => {

  const [isLoading, setIsLoading] = useState(false)
  const { selectedOrganisation } = useOrganisationContext()

  const { data: counts } = useQuery(["invoices/get_pagination_data", "ACCREC"], () => xero.get("invoices/get_pagination_data", {
    params: {
      raw: `TYPE="ACCREC"`
    }
  }).get("data"), {
    refetchOnMount: true,
  });

  const { data, refetch } = useNonReportedTotals();

  const xero = XeroClient();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const uploadFile = async (file) => {
    try {
      const content = await readFileAsync(file);
      setIsLoading(true)
      const parsedData = parseXlsx(content);
      const filteredData = parsedData.filter((row) => {
        const contactName = row.Contact?.Name?.trim();
        const date = row.Date?.trim();
        const itemCode = row.LineItems?.[0]?.ItemCode?.trim();
        const description = row.LineItems?.[0]?.Description?.trim();

        return contactName || itemCode || description || date;
      });
      const response = await xero.post('invoices', filteredData);
      setIsLoading(false)
      NotificationManager.success('Invoices uploaded successfully');
      setTimeout(() => {
        setIsModalOpen(false);
      }, 800);
    } catch (error) {
      setIsLoading(false)
      let errorCount = 0;

      if (error && error.response && error.response.data && error.response.data.detail) {
        error.response.data.detail.forEach((detail, index) => {
          if (errorCount < 3) {
            const errorMessage = `${index + 1}. ${detail}`;
            NotificationManager.error(errorMessage, 'Error');
            errorCount++;
          }
        });
      } else {
        if (errorCount < 3) {
          const errorMessage = 'Error uploading file, check console for more info.';
          NotificationManager.error(errorMessage);
          errorCount++;
        }
      }
    }
  };

  const parseXlsx = (xlsxContent) => {
    const data = new Uint8Array(xlsxContent);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });

    const headerRow = jsonData[0];

    return jsonData.slice(1).map((row) => {
      const invoiceNumber = row[headerRow.indexOf("InvoiceNumber")] === "INV-AUTO" || row[headerRow.indexOf("InvoiceNumber")] === "" ? undefined : row[headerRow.indexOf("InvoiceNumber")];
      const status = row[headerRow.indexOf("Status")] === "PAID" ? "AUTHORISED" : row[headerRow.indexOf("Status")] === "AUTHORISED" || row[headerRow.indexOf("Status")] === "SUBMITTED" ? row[headerRow.indexOf("Status")] : "DRAFT";

      return {
        Type: 'ACCREC',
        ...(invoiceNumber ? { InvoiceNumber: invoiceNumber } : {}),
        Contact: { Name: row[headerRow.indexOf("ContactName*")] },
        LineItems: [
          {
            ItemCode: row[headerRow.indexOf("ItemCode")],
            Description: row[headerRow.indexOf("Description")],
            Quantity: row[headerRow.indexOf("Quantity*")],
            UnitAmount: row[headerRow.indexOf("UnitPrice*")],
            AccountCode: row[headerRow.indexOf("AccountCode")],
            TaxType: row[headerRow.indexOf("TaxType")],
          },
        ],
        Date: row[headerRow.indexOf("Date*")],
        DueDate: row[headerRow.indexOf("DueDate*")],
        Reference: row[headerRow.indexOf("Reference")],
        Status: status,
      };
    });
  };

  return (
    <Page className="inv-tabs nw-list">
      {isLoading &&
        createPortal(
          <div className="content">
            <div className="loader"></div>
            {<LoadingMsg />}
          </div>,
          document.querySelector('#loader-holder')
        )}
      <div>
        <div className="d-flex align-items-center">
          <Link to={`${match.url}/edit/new`} className="btn btn-primary ml-0">
            <Trans>New Invoice</Trans>
            <span className="mx-1" />
            <span className="fa fa-plus" />
          </Link>
          <Button color="primary" onClick={openModal}>
            <Trans>Import Invoices</Trans><span className="mx-1" /><i className="fas fa-file-excel" />
          </Button>
          <div>
            <ImportModal
              isOpen={isModalOpen}
              toggle={closeModal}
              title="Invoices"
              importantNotes={importantNotes}
              handleUpload={uploadFile}
            />
          </div>
        </div>
        <Nav tabs className="nw-tabs2 inv-tabs my-3">
          <NavItem to={`all`} translate={false}><Trans>All</Trans> {renderCountSpan(counts?.All?.itemCount)}</NavItem>
          <NavItem to={`draft`} translate={false}><Trans>Draft</Trans> {renderCountSpan(counts?.DRAFT?.itemCount)}</NavItem>
          <NavItem to={`awaiting_approval`} translate={false}><Trans>Awaiting Approval</Trans> {renderCountSpan(counts?.SUBMITTED?.itemCount)}</NavItem>
          <NavItem to={`approved`} translate={false}><Trans>Awaiting Payment</Trans> {renderCountSpan(counts?.AUTHORISED?.itemCount)}</NavItem>
          <NavItem to={`paid`} translate={false}><Trans>Paid</Trans> {renderCountSpan(counts?.PAID?.itemCount)}</NavItem>
          {selectedOrganisation.onboarding_date &&
            <NavItem to={`${match.url}/reporting`} translate={false} className="position-relative">
              <Trans>Zatca Reporting</Trans> ({data?.invoices?.itemCount})
              <Warning total_non_reported={data?.invoices?.itemCount} />
            </NavItem>
          }
        </Nav>

        <TabContent>
          <Switch>
            <TabPane path={`${match.url}/all`} component={All} />
            <TabPane path={`${match.url}/draft`} component={Draft} />
            <TabPane
              path={`${match.url}/awaiting_approval`}
              component={AwaitingApproval}
            />
            <TabPane path={`${match.url}/approved`} component={Approved} />
            <TabPane path={`${match.url}/paid`} component={Paid} />
            <TabPane path={`${match.url}/reporting`} component={Reporting} isReporting={true} refetch_non_reported={refetch} />
            <Redirect to={`${match.url}/all`} />
          </Switch>
        </TabContent>
      </div>
    </Page>
  )
};

export const enhance = compose();

export default enhance(List);
